// Slice 12 — TOTP enroll-or-verify for CGC members (DESIGN §5.5). Mirror of
// [`AdminTotp`](./AdminTotp.tsx). Lands here from the `/cgc/verify`
// interstitial (which POSTed `/auth/cgc/verify` to redeem the magic-link
// token) with a `cid` (challenge id) + `stage` query param. Enroll: server
// shows the proposed secret + otpauth URI for the adviser to scan into their
// authenticator app, then verify the first code. Verify: adviser enters the
// current code; server checks against the stored secret.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { QRCodeSVG } from 'qrcode.react';
import { Logo } from '@/components/brand/Logo.tsx';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput } from '@/components/ui/Field.tsx';

interface ChallengeResponse {
  stage: 'enroll' | 'verify';
  enrollment?: { secretBase32: string; otpauthUri: string };
}

export function CgcTotp() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const cid = params.get('cid') ?? '';

  const [challenge, setChallenge] = useState<ChallengeResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!cid) {
      setLoadError('No challenge id — go back to /cgc/sign-in and request a new link.');
      return;
    }
    api
      .get<ChallengeResponse>(`/auth/cgc/challenge/${cid}`)
      .then(setChallenge)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          setLoadError('This challenge has expired. Request a new sign-in link.');
        } else {
          setLoadError('Could not load the challenge.');
        }
      });
  }, [cid]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setPending(true);
    try {
      await api.post<{ ok: true }>('/auth/cgc/totp/verify', {
        challengeId: cid,
        code,
      });
      await navigate('/cgc');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401 && err.message === 'invalid_code') {
          setSubmitError('That code didn’t match. Try the next one from your authenticator.');
        } else if (err.status === 429 && err.message === 'too_many_attempts') {
          setSubmitError('Too many wrong codes. Request a new sign-in link to try again.');
        } else if (err.status === 401) {
          setSubmitError('This challenge has expired. Request a new sign-in link.');
        } else {
          setSubmitError('Something went wrong. Try again.');
        }
      } else {
        setSubmitError('Network error.');
      }
    } finally {
      setPending(false);
    }
  }

  if (loadError) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <h1 className="text-2xl font-semibold tracking-tight">Sign-in</h1>
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          {loadError}
        </p>
        <p className="mt-4 text-sm">
          <a className="underline" href="/cgc/sign-in">
            Back to CGC sign-in
          </a>
        </p>
      </main>
    );
  }

  if (!challenge) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-6 py-12">
        <p className="text-muted-foreground">Loading…</p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
      <Logo className="text-2xl" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">
        {challenge.stage === 'enroll' ? 'Set up two-factor auth' : 'Enter your two-factor code'}
      </h1>

      {challenge.stage === 'enroll' && challenge.enrollment ? (
        <div className="mt-6 space-y-4 rounded-lg border border-border bg-muted/30 p-5 text-sm">
          <p>
            Add this account to your authenticator app (1Password, Authy, Google Authenticator…).
            Either scan the QR with your phone, or enter the base32 secret manually.
          </p>
          <div className="flex justify-center">
            <div className="rounded-md border border-border bg-white p-3">
              <QRCodeSVG
                value={challenge.enrollment.otpauthUri}
                size={192}
                level="M"
                aria-label="TOTP enrolment QR code"
              />
            </div>
          </div>
          <div>
            <p className="text-xs tracking-wide text-muted-foreground uppercase">
              Manual-entry secret
            </p>
            <code className="mt-1 block rounded border border-border bg-background p-2 font-mono text-sm tracking-widest">
              {challenge.enrollment.secretBase32}
            </code>
          </div>
          <p className="text-xs text-muted-foreground">
            The secret is shown once. After you verify the first code, only an encrypted copy is
            kept server-side.
          </p>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="mt-6 grid gap-5">
        <Field
          label="Six-digit code"
          hint="From your authenticator app. Codes refresh every 30 seconds."
        >
          {(id, descProps) => (
            <TextInput
              id={id}
              {...descProps}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              pattern="\d{6}"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          )}
        </Field>
        <Button type="submit" loading={pending}>
          {challenge.stage === 'enroll' ? 'Verify and finish setup' : 'Sign in'}
        </Button>
        {submitError ? (
          <p className="text-sm text-traffic-red" role="alert">
            {submitError}
          </p>
        ) : null}
      </form>
    </main>
  );
}
